import keyBy from 'lodash/keyBy';
import { delay, takeEvery, takeLatest, select, call, put } from 'redux-saga/effects';
import { browserHistory } from 'react-router';
import { change, arrayRemove } from 'redux-form';

import * as sagaCommon from '../../sagasCommon';
import * as apiCommon from '../../../services/apiCommon';

import {
  getDefaultTransactionStatus,
  getFormLines,
} from '../../../components/admin/inventory_management/adjustment/InventoryAdjustmentSelector';
import * as apiInventoryAdjustment from '../../../services/inventory/adjustment/api_inventory_adjustment';
import {
  GET_INVENTORY_ADJUSTMENT,
  DELETE_INVENTORY_ADJUSTMENT,
  DELETE_INVENTORY_ADJUSTMENT_FROM_LIST,
  FETCH_LIST_INVENTORY_ADJUSTMENT,
  GET_INVENTORY_ADJUSTMENT_LINE_ITEM_LOCATION,
  GET_ALL_INVENTORY_ADJUSTMENT_LINE_ITEM_LOCATION,
  SAVE_INVENTORY_ADJUSTMENT,
} from '../../../constants/constant_saga_types';
import {
  INVENTORY_ADJUSTMENT,
  INVENTORY_MOVEMENT_CONTROL,
} from '../../../constants/constant_doc_types';
import {
  PENDING_APPROVAL,
  OPEN,
} from '../../../constants/constant_transaction_status';
import { InventoryAdjustmentForm as form } from '../../../constants/constant_form_names';
import { getPreferenceSettings } from '../../../actions/actionCreatorsAdmin';

export function* getInventoryAdjustmentSaga(feathersApp) {
  yield takeEvery(
    GET_INVENTORY_ADJUSTMENT,
    callGetInventoryAdjustment,
    feathersApp,
  );
}

function* callGetInventoryAdjustment(feathersApp, action) {
  try {
    const { user } = yield select();
    const { id } = action;

    yield sagaCommon.showProgressDataById();

    yield put(getPreferenceSettings(['enable_item_part_quantity']));

    if (id) {
      const result = yield call(
        apiInventoryAdjustment.fetchInventoryAdjustmentById,
        feathersApp,
        {
          id,
          user,
        },
      );
      let data = { ...result };

      if (
        result.status.cid === OPEN ||
        result.status.cid === PENDING_APPROVAL
      ) {
        data.enter_unit_cost = result.lines.some((line) => {
          return !!line.unit_cost;
        });
      }
      yield sagaCommon.doneDataById(data);
    } else {
      const defaultTransactionStatus = yield select(
        getDefaultTransactionStatus,
      );

      yield sagaCommon.doneDataById({
        status_id: defaultTransactionStatus.id,
        status: defaultTransactionStatus,
      });
    }
  } catch (e) {
    yield sagaCommon.showErrorDataById(e, true);
  }
}

export function* deleteInventoryAdjustmentSaga(feathersApp) {
  yield takeEvery(
    DELETE_INVENTORY_ADJUSTMENT,
    callDeleteInventoryAdjustment,
    feathersApp,
  );
}

export function* deleteInventoryAdjustmentFromListSaga(feathersApp) {
  yield takeEvery(
    DELETE_INVENTORY_ADJUSTMENT_FROM_LIST,
    callDeleteInventoryAdjustmentFromList,
    feathersApp,
  );
}

function* callDeleteInventoryAdjustment(feathersApp, action) {
  const result = yield deleteInventoryAdjustment(feathersApp, action);
  yield delay(2000);
  if (result) {
    browserHistory.push('/admin/inventory/adjustment');
  }
}

function* callDeleteInventoryAdjustmentFromList(feathersApp, action) {
  yield deleteInventoryAdjustment(feathersApp, action);
  yield callFetchListInventoryAdjustment(feathersApp, { silent: true });
}

function* deleteInventoryAdjustment(feathersApp, action) {
  yield sagaCommon.showProgressDeleteRecord();
  try {
    const { id } = action;
    let result = null;
    if (id) {
      const state = yield select();
      const { tenant, user } = state;
      const params = { tenant, user, id };
      result = yield call(
        apiInventoryAdjustment.deleteInventoryAdjustment,
        feathersApp,
        params,
      );
      yield sagaCommon.doneDeleteRecord(id, INVENTORY_MOVEMENT_CONTROL);
    }
    return result;
  } catch (error) {
    yield sagaCommon.showErrorDeleteRecord();
  }
}

export function* saveInventoryAdjustmentSaga(feathersApp) {
  yield takeEvery(
    SAVE_INVENTORY_ADJUSTMENT,
    callSaveInventoryAdjustment,
    feathersApp,
  );
}

function* callSaveInventoryAdjustment(feathersApp, action) {
  try {
    const { user } = yield select();
    const { data, redirectToView } = action;

    yield sagaCommon.showProgressSaveRecord();

    const result = yield call(
      apiInventoryAdjustment.saveInventoryAdjustment,
      feathersApp,
      {
        user,
        data,
      },
    );

    if (result && result.id) {
      yield put({ type: GET_INVENTORY_ADJUSTMENT, id: result.id });
    }

    if (redirectToView) {
      browserHistory.push(`/admin/inventory/adjustment/view/${result.id}`);
    } else {
      browserHistory.push(`/admin/inventory/adjustment/edit/${result.id}`);
    }

    yield sagaCommon.doneSaveRecord(result, 'Inventory adjustment was saved');
  } catch (e) {
    yield sagaCommon.showErrorSaveRecord(e);
  }
}

export function* fetchListInventoryAdjustmentSaga(feathersApp) {
  yield takeLatest(
    FETCH_LIST_INVENTORY_ADJUSTMENT,
    callFetchListInventoryAdjustment,
    feathersApp,
  );
}

function* callFetchListInventoryAdjustment(feathersApp, action) {
  try {
    const { silent, filters } = action;
    yield sagaCommon.showProgressListTable(silent);
    const dataParams = {
      filters: { ...filters },
      list_type: INVENTORY_ADJUSTMENT,
    };
    const tableData = yield call(apiCommon.getDocs, feathersApp, dataParams);
    yield call(sagaCommon.doneListTable, {
      rows: tableData.data,
      total_rows: tableData.total,
    });
  } catch (e) {
    yield sagaCommon.showErrorListTable(e);
  }
}

export function* getInventoryAdjustmentLineItemLocationSaga(feathersApp) {
  yield takeEvery(
    GET_INVENTORY_ADJUSTMENT_LINE_ITEM_LOCATION,
    callGetInventoryAdjustmentLineItemLocationSaga,
    feathersApp,
  );
}

function* callGetInventoryAdjustmentLineItemLocationSaga(feathersApp, action) {
  try {
    const { user } = yield select();
    const { locationId, itemId, index, itemUom } = action;
    yield put(change(form, `lines[${index}].isFetchingItemLocation`, true));
    const itemLocation = yield call(
      apiInventoryAdjustment.getItemLocationById,
      feathersApp,
      {
        itemId: itemId.split(':')[0],
        locationId,
        user,
      },
    );

    if (itemLocation) {
      if (itemUom) {
        const uomQty = Math.floor(itemLocation.on_hand/itemUom.conversion_rate);
        yield put(
          change(
            form,
            `lines[${index}].uom_current_quantity`,
            Number(uomQty),
          ),
        );
        yield put(
          change(
            form,
            `lines[${index}].uom_new_quantity`,
            Number(uomQty),
          ),
        );
      }
      yield put(
        change(
          form,
          `lines[${index}].current_average_cost`,
          Number(itemLocation.average_cost),
        ),
      );
      yield put(
        change(
          form,
          `lines[${index}].current_quantity`,
          Number(itemLocation.on_hand),
        ),
      );
      yield put(
        change(
          form,
          `lines[${index}].new_quantity`,
          Number(itemLocation.on_hand),
        ),
      );
      yield put(
        change(
          form,
          `lines[${index}].total_cost`,
          Number(itemLocation.on_hand_total_cost),
        ),
      );
      yield put(change(form, `lines[${index}].quantity`, 0));
      yield put(change(form, `lines[${index}].isFetchingItemLocation`, false));
    } else {
      yield call(resetLine, index);
      if (!locationId) {
        throw new Error('No location selected');
      }
      throw new Error('Item is not available in location');
    }
  } catch (e) {
    yield sagaCommon.showErrorSaveRecord(e);
  }
}

function* resetLine(index) {
  yield put(arrayRemove(form, 'lines', index));
}

export function* getAllInventoryAdjustmentLinesItemLocationSaga(feathersApp) {
  yield takeLatest(
    GET_ALL_INVENTORY_ADJUSTMENT_LINE_ITEM_LOCATION,
    callGetAllInventoryAdjustmentLinesItemLocationSaga,
    feathersApp,
  );
}

function* callGetAllInventoryAdjustmentLinesItemLocationSaga(
  feathersApp,
  action,
) {
  try {
    const lines = yield select(getFormLines);
    const { locationId } = action;
    const itemIds = lines.map((line) => line.item_id);

    const itemLocations = yield call(
      apiInventoryAdjustment.getItemLocationsByIds,
      feathersApp,
      {
        itemIds,
        locationId,
      },
    );

    if (!itemLocations.length) {
      yield put(change(form, 'lines', []));
      throw new Error('Item is not available in location');
    }

    const indexedItemLocations = keyBy(
      itemLocations,
      (itemLocation) => `${itemLocation.item_id}:${itemLocation.location_id}`,
    );

    const newLines = [];

    lines.forEach((line) => {
      const itemLocation =
        indexedItemLocations[`${line.item_id}:${locationId}`];

      newLines.push({
        ...line,
        current_average_cost: Number(itemLocation.average_cost),
        current_quantity: Number(itemLocation.on_hand),
        new_quantity: Number(itemLocation.on_hand),
        total_cost: Number(itemLocation.on_hand_total_cost),
        quantity: 0,
      });
    });

    yield put(change(form, 'lines', newLines));
  } catch (e) {
    yield sagaCommon.showErrorSaveRecord(e);
  }
}
